import { useState, useRef, useEffect } from "react";
import ThreeDotsVerticalIcon from "../../../../../../../assets/svg/three-dots-vertical-icon";
import { HelpDeskTicket } from "../utils/types";
import { TicketCardActionMenu } from "./TicketCardActionMenu";
import { ShareTicketModal } from "./ShareTicketModal";
import DeleteTicketModal from "./DeleteTicketModal";
import { customToast } from "../../../../../../../utils/custom-toast/custom-toast";
import { useDeleteTicketMutation } from "../../../../../../../redux/services/helpdeskApi";
import { useHasHelpdeskUpdateAccess } from "../../../utils/helpdeskPermissions";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../../../redux/store";
import { getHelpdeskLabels } from "../../../../../../../utils/constants/mappings";

interface TicketTableRowActionsProps {
  ticket: HelpDeskTicket;
  onTicketDeleted: (ticketId: number) => void;
}

export const TicketTableRowActions = ({
  ticket,
  onTicketDeleted,
}: TicketTableRowActionsProps) => {
  const ticketId = ticket?.ticket_id;
  const [showActionMenu, setShowActionMenu] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);
  const [deleteTicket, { isLoading: isDeleting }] = useDeleteTicketMutation();
  const canUpdate = useHasHelpdeskUpdateAccess(ticket?.ticket_category);
  const region = useSelector((state: RootState) => state?.auth?.client?.region);
  const helpdeskLabels = getHelpdeskLabels(region);

  const handleConfirmDelete = async () => {
    try {
      await deleteTicket(String(ticketId))?.unwrap();
      onTicketDeleted(ticketId);
      customToast(helpdeskLabels.ticketDeletedSuccess, "success");
      setShowDeleteModal(false);
    } catch {
      customToast(helpdeskLabels.failedToDeleteTicket, "error");
    }
  };

  const handleThreeDotsClick = (e: React.MouseEvent) => {
    e?.stopPropagation();
    const willOpen = !showActionMenu;
    if (willOpen) {
      window.dispatchEvent(
        new CustomEvent("close-ticket-menus", {
          detail: ticketId,
        })
      );
    }
    setShowActionMenu(willOpen);
  };

  useEffect(() => {
    const handleCloseMenus = (e: Event) => {
      e?.stopPropagation();
      if ((e as CustomEvent)?.detail !== ticketId) {
        setShowActionMenu(false);
      }
    };
    window.addEventListener("close-ticket-menus", handleCloseMenus);
    return () =>
      window.removeEventListener("close-ticket-menus", handleCloseMenus);
  }, [ticketId]);

  return (
    <div className="flex items-center justify-end gap-0.5 px-2">
      <div
        ref={buttonRef}
        data-three-dots-button
        onClick={handleThreeDotsClick}
        className="group/more flex items-center justify-center p-1 rounded hover:bg-hover-effect dark:hover:bg-hover-effect-dk transition-colors cursor-pointer"
        aria-label="More options"
      >
        <ThreeDotsVerticalIcon className="w-[18px] h-[18px] text-gray-placeholder dark:text-gray-placeholder-dk group-hover/more:text-verba-indigo dark:group-hover/more:text-verba-indigo-dk transition-colors" />
      </div>

      {showActionMenu && (
        <TicketCardActionMenu
          ticket={ticket}
          triggerRef={buttonRef}
          onDeleteClick={() => {
            setShowActionMenu(false);
            setShowDeleteModal(true);
          }}
          onClose={() => setShowActionMenu(false)}
          onShare={() => setShowShareModal(true)}
          canUpdate={canUpdate}
        />
      )}
      <ShareTicketModal
        ticketId={ticketId}
        open={showShareModal}
        onOpenChange={setShowShareModal}
      />
      <DeleteTicketModal
        open={showDeleteModal}
        onOpenChange={setShowDeleteModal}
        onConfirmDelete={handleConfirmDelete}
        ticket={ticket}
        isDeleting={isDeleting}
      />
    </div>
  );
};
